import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import { getDriversByBoss } from "../../services/driverService";
import { createTurno } from "../../services/turnoCreationService";
import { getStoredUserType } from "../../services/authService";

const PALETTE_DARK = {
  cardBg: "#1E293B",
  cardBorder: "#334155",
  text: "#F1F5F9",
  title: "#E2E8F0",
  accent: "#38BDF8",
  btn: "#3B82F6",
  btnHover: "#2563EB",
};
const PALETTE_LIGHT = {
  cardBg: "#ECECEC",
  cardBorder: "#E5E7EB",
  text: "#1E293B",
  title: "#111827",
  accent: "#38BDF8",
  btn: "#2563EB",
  btnHover: "#1D4ED8",
};

const cleanString = (val) =>
  typeof val === "string" ? val.trim().replace(/^['\"]+|['\"]+$/g, "") : val;

const parseNumber = (raw) => {
  if (raw === null || raw === undefined) return 0;
  const cleaned = String(raw).replace(/\./g, "").replace(",", ".");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const toISOorNull = (value) => {
  const d = value ? new Date(value) : null;
  return d && !isNaN(d.getTime()) ? d.toISOString() : null;
};

const parsePoint = (raw) => {
  if (!raw || typeof raw !== "string") return null;

  const trimmed = raw.trim();
  if (!trimmed) return null;

  const pattern =
    /(\d+(?:\.\d+)?)\s*[°:\s]\s*([\d.]+)\s*['’]?\s*([NS])?[\s,]+(\d+(?:\.\d+)?)\s*[°:\s]\s*([\d.]+)\s*['’]?\s*([EW])?/i;
  const match = trimmed.match(pattern);
  if (!match) {
    return { raw: trimmed };
  }
  const [, degLat, minLat, hemiLat, degLon, minLon, hemiLon] = match;
  const lat = (Number(degLat) || 0) + (Number(minLat) || 0) / 60;
  const lon = (Number(degLon) || 0) + (Number(minLon) || 0) / 60;
  const finalLat = hemiLat && hemiLat.toUpperCase() === "S" ? -lat : lat;
  const finalLon = hemiLon && hemiLon.toUpperCase() === "W" ? -lon : lon;
  if (!Number.isFinite(finalLat) || !Number.isFinite(finalLon)) {
    return { raw: trimmed };
  }
  return {
    type: "Point",
    coordinates: [finalLon, finalLat],
    raw: trimmed,
  };
};

const parseSmartTdDate = (raw) => {
  if (!raw) return null;
  const cleaned = cleanString(raw);
  const match = cleaned.match(/(\d{2})\/(\d{2})\/(\d{2,4})\s+(\d{2}):(\d{2})/);
  if (!match) return null;
  const [, dd, mm, yy, hh, min] = match;
  const year = yy.length === 2 ? 2000 + Number(yy) : Number(yy);
  const date = new Date(Date.UTC(year, Number(mm) - 1, Number(dd), Number(hh), Number(min)));
  return isNaN(date.getTime()) ? null : date.toISOString();
};

const parseCsvLine = (line = "", sep = ",") => {
  const cells = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const next = line[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === sep && !inQuotes) {
      cells.push(current.trim());
      current = "";
      continue;
    }

    current += char;
  }

  cells.push(current.trim());
  return cells;
};

const simpleCsvToRows = (text = "") => {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
  if (!lines.length) return [];
  const sep = lines[0].includes(";") ? ";" : ",";
  const headers = parseCsvLine(lines[0], sep).map((h) => h.trim());
  return lines.slice(1).map((line) => {
    const cells = parseCsvLine(line, sep);
    const row = {};
    headers.forEach((h, idx) => {
      row[h] = cells[idx] ?? "";
    });
    return row;
  });
};

const parseServicesFromCsv = (text = "") => {
  const rows = simpleCsvToRows(text);
  if (!rows.length) return [];

  const services = [];
  rows.forEach((row, idx) => {
    const id = cleanString(row["SERVICIO"]) || `svc_${idx}`;
    if (!id) return;
    const metodo = cleanString(row["MÉTODO DE PAGO"] || row["METODO DE PAGO"] || row["pago"]) || "Efectivo";
    const distancia = parseNumber(row["DISTANCIA km."]);
    const trayectoDistanciaKm = distancia;
    const tarifa = cleanString(row["TARIFA"]);
    const tipoConcertado = cleanString(row["TIPO CONCERTADO"]);
    const tipoServicio = cleanString(row["TIPO DE SERVICIO"]);
    const direccionInicio = cleanString(row["DIRECCIÓN INICIO"] || row["DIRECCION INICIO"]);
    const direccionFin = cleanString(row["DIRECCIÓN FIN"] || row["DIRECCION FIN"]);
    const posIni = parsePoint(cleanString(row["POSICIÓN INICIO"] || row["POSICION INICIO"]));
    const posFin = parsePoint(cleanString(row["POSICIÓN FIN"] || row["POSICION FIN"]));
    const fechaInicio = parseSmartTdDate(row["FECHA INICIO"]) || toISOorNull(row["FECHA INICIO"]);
    const fechaFin = parseSmartTdDate(row["FECHA FIN"]) || toISOorNull(row["FECHA FIN"]);
    const taximetro = parseNumber(row["IMPORTE TAXÍMETRO EUR"] || row["IMPORTE TAXIMETRO EUR"]);
    const suplementos = parseNumber(row["SUPLEMENTOS EUR"]);
    const propinas = parseNumber(row["PROPINAS EUR"]);
    const peajes = parseNumber(row["PEAJES EUR"]);
    const total = parseNumber(row["TOTAL EUR"] || row["IMPORTE TOTAL EUR"]);
    const velocidadMaxima = parseNumber(row["Velocidad máxima (km/h)"]);
    const velocidadMaximaDesdeUltimo = parseNumber(row["Velocidad máxima desde último servicio (km/h)"]);

    services.push({
      externalId: id,
      fechaInicio: fechaInicio || new Date().toISOString(),
      fechaFin: fechaFin || null,
      metodoPago: metodo,
      distanciaKm: distancia,
      trayectoDistanciaKm,
      importeTotal: total || taximetro,
      taximetro: taximetro || total,
      tipoServicio,
      tipoConcertado,
      direccionInicio,
      direccionFin,
      tarifa,
      posicionInicio: posIni,
      posicionFin: posFin,
      importes: {
        total: total || taximetro,
        taximetro: taximetro || total,
        suplementos,
        propinas,
        peajes,
      },
      velocidadMaxima,
      velocidadMaximaDesdeUltimo,
    });
  });

  return services;
};

const safeNumber = (val) => {
  const parsed = Number(String(val || "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : "";
};

const parseCsvText = (text = "") => {
  const clean = text.trim();
  if (!clean) return {};

  const lines = clean.split(/\r?\n/).filter(Boolean);
  if (!lines.length) return {};

  const sep = lines[0].includes(";") ? ";" : ",";
  const headers = parseCsvLine(lines[0], sep).map((h) => h.trim().toLowerCase());
  const values = parseCsvLine(lines[1] || "", sep).map((v) => v.trim());
  const map = {};
  headers.forEach((h, idx) => {
    map[h] = values[idx] ?? "";
  });

  const pick = (...keys) => {
    for (const key of keys) {
      if (map[key] !== undefined && map[key] !== "") return map[key];
    }
    return "";
  };

  return {
    startKm: safeNumber(
      pick("km tomar", "km inicio", "kmtomar", "kmstart", "startkm", "start_km")
    ),
    endKm: safeNumber(pick("km dejar", "km fin", "kmdejar", "kmend", "endkm", "end_km")),
    startServices: safeNumber(
      pick("servicios tomar", "servicios inicio", "servicios_start", "services_start")
    ),
    endServices: safeNumber(
      pick("servicios dejar", "servicios fin", "servicios_end", "services_end")
    ),
    startIncome: safeNumber(
      pick(
        "recaudacion tomar",
        "recaudación tomar",
        "recaudacion inicio",
        "startincome",
        "ingresos inicio",
        "income_start"
      )
    ),
    endIncome: safeNumber(
      pick(
        "recaudacion dejar",
        "recaudación dejar",
        "recaudacion fin",
        "endincome",
        "ingresos fin",
        "income_end"
      )
    ),
  };
};

const NuevoTurno = () => {
  const navigate = useNavigate();
  const userType = useSelector((state) => state.user.type) || getStoredUserType();
  const driverIdFromStore = useSelector((state) => state.user.driverId);
  const licensesFromStore = useSelector((state) => state.user.licenses) || [];

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const palette = useMemo(
    () => (isDark ? PALETTE_DARK : PALETTE_LIGHT),
    [isDark]
  );
  const isDesktop = useMediaQuery("(min-width:900px)");
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.04)"
          : theme.palette.background.paper,
      "& fieldset": {
        borderColor: theme.palette.divider,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.divider,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 1.5,
      },
    },
    "& .MuiInputLabel-root": {
      "&.Mui-focused": {
        color: theme.palette.text.primary,
      },
    },
  };

  const [drivers, setDrivers] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [form, setForm] = useState({
    driverId: "",
    licenseId: licensesFromStore?.[0]?._id || "",
    date: new Date().toISOString().slice(0, 10),
    startKm: "",
    endKm: "",
    startServices: "",
    endServices: "",
    startIncome: "",
    endIncome: "",
    creditIncome: "",
    cardIncome: "",
    gas: "",
  });
  const [csvText, setCsvText] = useState("");
  const [csvName, setCsvName] = useState("");
  const [importedServices, setImportedServices] = useState([]);
  const [successMsg, setSuccessMsg] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchDrivers = async () => {
      if (userType !== "Boss") return;
      try {
        setLoadingDrivers(true);
        const list = await getDriversByBoss();
        setDrivers(
          (list || []).map((d) => ({
            _id: d._id,
            name: d.name || d.fullName || d.username || "Sin nombre",
            licenses: d.licenses || [],
          }))
        );
      } catch (e) {
        setError(e.message || "No se pudieron cargar los conductores");
      } finally {
        setLoadingDrivers(false);
      }
    };
    fetchDrivers();
  }, [userType]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setCsvName(file.name);

    const reader = new FileReader();
    reader.onload = ({ target }) => {
      const text = target?.result || "";
      setCsvText(text);
      const parsed = parseCsvText(text);
      setForm((prev) => ({ ...prev, ...parsed }));
      const svcs = parseServicesFromCsv(text);
      setImportedServices(svcs);
      console.info("[WEB][NuevoTurno] Servicios importados", {
        length: svcs.length,
        sample: svcs.slice(0, 2),
      });
    };
    reader.readAsText(file);
  };

  const selectedDriverId = userType === "Boss" ? form.driverId : driverIdFromStore;
  const availableLicenses = useMemo(() => {
    if (userType === "Boss") {
      return drivers.find((d) => String(d._id) === String(selectedDriverId))?.licenses || [];
    }
    return licensesFromStore || [];
  }, [drivers, licensesFromStore, selectedDriverId, userType]);

  useEffect(() => {
    setForm((prev) => {
      const stillValid =
        prev.licenseId && availableLicenses.find((l) => String(l._id) === String(prev.licenseId));
      if (stillValid) return prev;
      if (availableLicenses.length === 1) {
        return { ...prev, licenseId: availableLicenses[0]._id };
      }
      if (prev.licenseId) {
        return { ...prev, licenseId: "" };
      }
      return prev;
    });
  }, [availableLicenses]);

  const validations = useMemo(() => {
    const errors = [];
    if (userType === "Boss" && !form.driverId) errors.push("Selecciona un conductor.");
    if (!form.date) errors.push("Selecciona la fecha del turno.");
    if (availableLicenses.length > 1 && !form.licenseId) errors.push("Selecciona la licencia.");
    if (form.startKm === "" || form.endKm === "") errors.push("Completa los kilómetros al tomar y dejar.");
    if (form.startIncome === "" || form.endIncome === "") errors.push("Completa la recaudación al tomar y dejar.");
    return errors;
  }, [availableLicenses.length, form.date, form.driverId, form.licenseId, form.endIncome, form.endKm, form.startIncome, form.startKm, userType]);

  const handleSubmit = async () => {
    setSuccessMsg("");
    if (validations.length) {
      setError(validations.join(" "));
      return;
    }
    try {
      setSubmitting(true);
      const resolvedLicenseId = form.licenseId || (availableLicenses.length === 1 ? availableLicenses[0]._id : undefined);
      await createTurno({
        driverId: userType === "Boss" ? form.driverId : driverIdFromStore,
        licenseId: resolvedLicenseId,
        startKm: Number(form.startKm),
        endKm: Number(form.endKm),
        startServices: Number(form.startServices || 0),
        endServices: Number(form.endServices || 0),
        startIncome: Number(form.startIncome),
        endIncome: Number(form.endIncome),
        cardIncome: Number(form.cardIncome || 0),
        creditIncome: Number(form.creditIncome || 0),
        gas: Number(form.gas || 0),
        date: form.date ? new Date(form.date) : undefined,
        csvText: csvText || null,
        csvName: csvName || null,
        services: importedServices,
      });
      console.log("[WEB][NuevoTurno] Payload enviado", {
        driverId: userType === "Boss" ? form.driverId : driverIdFromStore,
        licenseId: resolvedLicenseId,
        startKm: Number(form.startKm),
        endKm: Number(form.endKm),
        startServices: Number(form.startServices || 0),
        endServices: Number(form.endServices || 0),
        startIncome: Number(form.startIncome),
        endIncome: Number(form.endIncome),
        cardIncome: Number(form.cardIncome || 0),
        creditIncome: Number(form.creditIncome || 0),
        gas: Number(form.gas || 0),
        date: form.date ? new Date(form.date) : undefined,
        services: importedServices,
      });
      setSuccessMsg("Turno guardado correctamente");
      setCsvText("");
      setCsvName("");
      setForm((prev) => ({
        ...prev,
        date: new Date().toISOString().slice(0, 10),
        licenseId: availableLicenses.length === 1 ? availableLicenses[0]._id : "",
        startKm: "",
        endKm: "",
        startServices: "",
        endServices: "",
        startIncome: "",
        endIncome: "",
        cardIncome: "",
        creditIncome: "",
        gas: "",
      }));
      setImportedServices([]);
      navigate("/turnos");
    } catch (err) {
      console.error("Error guardando turno", err);
      const detail = err?.message ? `Detalle técnico: ${err.message}` : "";
      setError(`No se ha podido guardar el turno. Revisa tu conexión o inténtalo de nuevo. ${detail}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        m: { xs: "12px", md: "20px" },
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Header title="Nuevo Turno" subtitle="Registra una nueva hoja de turno" />

      <Paper
        sx={{
          p: { xs: 1, md: 2 },
          borderRadius: 2,
          flex: "0 0 auto",
          backgroundColor: palette.cardBg,
          border: `1px solid ${palette.cardBorder}`,
          gap: 1.25,
          minHeight: "auto",
          maxWidth: isDesktop ? 1100 : 900,
          alignSelf: "center",
          mt: 1,
        }}
      >
        {successMsg && <Alert severity="success">{successMsg}</Alert>}
        {error && !validations.length && (
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {validations.length > 0 && (
          <Alert severity="warning">{validations.join(" ")}</Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.15fr 0.85fr" },
            gap: { xs: 1.5, md: 2 },
            mt: { xs: 0.5, md: 1 },
          }}
        >
          <Box>
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={1}
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip color="primary" label="Turno" />
                <Typography variant="body2" color="text.secondary">
                  Completa los datos del turno o importa un CSV para precargar
                </Typography>
              </Stack>

              {importedServices.length > 0 && (
                <Chip
                  color="success"
                  label={`Servicios importados: ${importedServices.length}`}
                  size="small"
                />
              )}
            </Stack>

            <Grid container spacing={{ xs: 1.5, md: 2 }}>
              {userType === "Boss" && (
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    size={isDesktop ? "medium" : "small"}
                    label="Conductor"
                    value={form.driverId}
                    onChange={handleChange("driverId")}
                    disabled={loadingDrivers}
                    sx={inputSx}
                  >
                    {drivers.map((d) => (
                      <MenuItem key={d._id} value={d._id}>
                        {d.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              {availableLicenses.length > 0 && (
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    size={isDesktop ? "medium" : "small"}
                    label="Licencia"
                    value={form.licenseId}
                    onChange={handleChange("licenseId")}
                    disabled={availableLicenses.length === 1}
                    helperText={
                      availableLicenses.length === 1
                        ? "Se usará la única licencia disponible"
                        : "Selecciona la licencia del turno"
                    }
                    sx={inputSx}
                  >
                    {availableLicenses.map((lic) => (
                      <MenuItem key={lic._id} value={lic._id}>
                        {lic.licenseNumber || "Sin número"}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size={isDesktop ? "medium" : "small"}
                  label="Fecha del turno"
                  type="date"
                  value={form.date}
                  onChange={handleChange("date")}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    onClick: (e) => e.target?.showPicker?.(),
                  }}
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size={isDesktop ? "medium" : "small"}
                  label="Km al tomar"
                  type="number"
                  value={form.startKm}
                  onChange={handleChange("startKm")}
                  inputProps={{ min: 0, step: "0.01" }}
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size={isDesktop ? "medium" : "small"}
                  label="Tarjetas de crédito"
                  type="number"
                  value={form.cardIncome}
                  onChange={handleChange("cardIncome")}
                  inputProps={{ min: 0, step: "0.01" }}
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size={isDesktop ? "medium" : "small"}
                  label="Crédito / Abonados (introducido)"
                  type="number"
                  value={form.creditIncome}
                  onChange={handleChange("creditIncome")}
                  inputProps={{ min: 0, step: "0.01" }}
                  helperText="Importe de servicios abonados según el usuario"
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size={isDesktop ? "medium" : "small"}
                  label="Gasolina"
                  type="number"
                  value={form.gas}
                  onChange={handleChange("gas")}
                  inputProps={{ min: 0, step: "0.01" }}
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size={isDesktop ? "medium" : "small"}
                  label="Km al dejar"
                  type="number"
                  value={form.endKm}
                  onChange={handleChange("endKm")}
                  inputProps={{ min: 0, step: "0.01" }}
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size={isDesktop ? "medium" : "small"}
                  label="Servicios al tomar"
                  type="number"
                  value={form.startServices}
                  onChange={handleChange("startServices")}
                  inputProps={{ min: 0, step: "1" }}
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size={isDesktop ? "medium" : "small"}
                  label="Servicios al dejar"
                  type="number"
                  value={form.endServices}
                  onChange={handleChange("endServices")}
                  inputProps={{ min: 0, step: "1" }}
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size={isDesktop ? "medium" : "small"}
                  label="Recaudación al tomar"
                  type="number"
                  value={form.startIncome}
                  onChange={handleChange("startIncome")}
                  inputProps={{ min: 0, step: "0.01" }}
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size={isDesktop ? "medium" : "small"}
                  label="Recaudación al dejar"
                  type="number"
                  value={form.endIncome}
                  onChange={handleChange("endIncome")}
                  inputProps={{ min: 0, step: "0.01" }}
                  sx={inputSx}
                />
              </Grid>
            </Grid>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Stack
              direction={isDesktop ? "column" : "row"}
              alignItems={isDesktop ? "flex-start" : "center"}
              justifyContent="space-between"
              spacing={1}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
                <Button
                  variant="contained"
                  size={isDesktop ? "medium" : "small"}
                  startIcon={<UploadFileIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  Importar CSV
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  style={{ display: "none" }}
                  onChange={handleFileSelect}
                />
                {csvText && (
                  <Typography variant="body2" color="text.secondary" noWrap>
                    CSV cargado: {csvName || "archivo manual"}
                  </Typography>
                )}
              </Stack>
              <Button
                variant="outlined"
                size={isDesktop ? "medium" : "small"}
                onClick={() => navigate(-1)}
                disabled={submitting}
                sx={{
                  textTransform: "none",
                  color: theme.palette.error.main,
                  borderColor: theme.palette.error.main,
                  alignSelf: isDesktop ? "flex-start" : "flex-end",
                  "&:hover": { borderColor: theme.palette.error.dark, color: theme.palette.error.dark },
                }}
              >
                Cancelar
              </Button>
            </Stack>

            {csvText && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1,
                  bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#f8fafc",
                  maxHeight: 180,
                  overflow: "auto",
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Vista previa CSV
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {csvText.substring(0, 700)}
                  {csvText.length > 700 ? "..." : ""}
                </Typography>
              </Paper>
            )}
          </Box>
        </Box>

        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          sx={{
            pt: { xs: 1, md: 1.5 },
          }}
        >
          <Button
            variant="contained"
            startIcon={<AddCircleIcon />}
            onClick={handleSubmit}
            disabled={submitting}
            size={isDesktop ? "medium" : "small"}
            sx={{ textTransform: "none", fontWeight: 700, minWidth: isDesktop ? 240 : 200 }}
          >
            Guardar turno
          </Button>
        </Stack>
      </Paper>

      <Dialog
        open={Boolean(error)}
        onClose={() => setError("")}
        fullWidth
        maxWidth="xs"
        BackdropProps={{
          sx: {
            backgroundColor: alpha("#0F172A", 0.55),
            backdropFilter: "blur(2px)",
          },
        }}
        PaperProps={{
          sx: {
            backgroundColor: palette.cardBg,
            border: `1px solid ${palette.cardBorder}`,
            borderRadius: 2.5,
            color: palette.text,
            boxShadow:
              palette === PALETTE_LIGHT
                ? "0 18px 60px rgba(2,6,23,0.12)"
                : "0 20px 70px rgba(0,0,0,0.55)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.5, fontWeight: 800, color: palette.title }}>Error</DialogTitle>
        <DialogContent dividers sx={{ borderColor: alpha(palette.cardBorder, 0.6), pt: 1.25 }}>
          <Typography sx={{ color: palette.text }}>{error}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            variant="contained"
            onClick={() => setError("")}
            autoFocus
            sx={{
              textTransform: "none",
              fontWeight: 800,
              backgroundColor: palette.btn,
              "&:hover": { backgroundColor: palette.btnHover },
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NuevoTurno;
